import { useState } from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { FaArrowLeft, FaUndo } from "react-icons/fa";

import "./Grids.css";
import { inactiveEmployee } from "../../data/interfaces";

// A delete locks the login for 1000 years; anything past this is that, not a
// real "try again later" lockout.
const PERMANENT_LOCK_YEAR = 2100;

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

const describeStatus = (user: inactiveEmployee) => {
  const lockoutEnd = user.lockoutEnd ? new Date(user.lockoutEnd) : null;
  const locked = lockoutEnd !== null && lockoutEnd > new Date();

  if (user.deletedDate) {
    return `Deleted ${formatDate(user.deletedDate)}`;
  }

  if (locked && lockoutEnd!.getFullYear() >= PERMANENT_LOCK_YEAR) {
    return "Locked (left over from a delete)";
  }

  if (locked) {
    return `Locked until ${formatDate(user.lockoutEnd!)}`;
  }

  return "";
};

/**
 * Users who cannot sign in - deleted or locked out - with a restore for each.
 */
function InactiveUsersGrid(props: {
  onData: inactiveEmployee[];
  onBack: () => void;
  onRestore: (id: number) => void;
  canRestore: boolean;
}) {
  const [confirmUser, setConfirmUser] = useState<inactiveEmployee | null>(null);

  const handleRestore = () => {
    if (confirmUser) {
      props.onRestore(confirmUser.employeeId);
    }
    setConfirmUser(null);
  };

  return (
    <div className="p-2">
      <div className="grid__title__container">
        <div className="grid__header__background">
          <div className="grid__title">
            <div>Deleted &amp; locked users</div>
            <div onClick={props.onBack} className="grid__addrecord__button">
              <FaArrowLeft style={{ fontSize: "14pt", marginRight: "1rem" }} />{" "}
              <p style={{ margin: 0 }}>Back to users</p>
            </div>
          </div>
        </div>
      </div>

      <div className="h-2" />
      <table className="grid__table">
        <thead>
          <tr>
            <th>Users Name</th>
            <th>Position</th>
            <th>Email</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {props.onData.length === 0 && (
            <tr>
              <td colSpan={5} style={{ textAlign: "center" }}>
                No deleted or locked users.
              </td>
            </tr>
          )}
          {props.onData.map((user) => (
            <tr
              key={user.employeeId}
              style={{
                backgroundColor: "rgb(245 249 251)",
                border: "1px solid rgb(216 229 237)",
              }}
            >
              <td>{user.employeeName}</td>
              <td>{user.position}</td>
              <td>{user.email}</td>
              <td>{describeStatus(user)}</td>
              <td>
                {props.canRestore && (
                  <button
                    className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
                    onClick={() => setConfirmUser(user)}
                  >
                    <FaUndo style={{ marginRight: "0.5rem" }} />
                    Restore
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {confirmUser && (
        <Dialog title={"Please confirm"} onClose={() => setConfirmUser(null)}>
          <p style={{ margin: "25px", textAlign: "center" }}>
            Restore {confirmUser.employeeName}? They will be able to log in
            again, with the groups and permissions they had before.
          </p>
          <DialogActionsBar>
            <button
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
              onClick={() => setConfirmUser(null)}
            >
              No
            </button>
            <button
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
              onClick={handleRestore}
            >
              Yes
            </button>
          </DialogActionsBar>
        </Dialog>
      )}
    </div>
  );
}

export default InactiveUsersGrid;
